import { useRef, useEffect, useCallback } from 'react';

import type { Course } from '@/api/entitties';
import { useVideoPlayerStore, useCurrentLecture } from '@/stores/videoPlayerStore';
import { useCoursesStore } from '@/stores/coursesStore';

/**
 * useVideoPlayer - A custom hook that connects React components to the video player Zustand store
 *
 * This hook provides a simplified interface to the video player functionality,
 * handling refs and effects while delegating state management to Zustand.
 *
 * @param selectedCourse - The course object containing sections and lectures
 * @returns An object with video player state and control functions
 */
export const useVideoPlayer = (selectedCourse: Course | null) => {
  const playerRef = useRef<unknown>(null);
  const isPlayingRef = useRef<boolean>(false);
  const hasCompletedRef = useRef<boolean>(false);

  const {
    currentSectionId,
    currentPlaybackId,
    muxTokens,
    isVideoLoading,
    shouldAutoplay,
    isPlaying,
    setCurrentCourse,
    setPlayerRef,
    setPlayingState,
    loadLecture,
    cleanup,
    reset,
  } = useVideoPlayerStore();

  const { toggleLectureCompletion } = useCoursesStore();
  const currentLecture = useCurrentLecture();

  const handlePlayerTimeUpdate = useCallback((event: Event) => {
    // Check if video is currently playing and update both ref and store
    const videoPlayer = event.target as HTMLVideoElement;
    const playing = videoPlayer && !videoPlayer.paused;

    if (playing !== isPlayingRef.current) {
      isPlayingRef.current = playing;
      setPlayingState(playing);
    }

    // Check for playback duration and mark lecture as completed if needed.
    // Ignore the cases when user messes with completeness checkbox and the video seek control back and forth.
    if (videoPlayer && !hasCompletedRef.current) {
      const progress = (videoPlayer.currentTime / videoPlayer.duration) * 100;
      if (progress >= 90 && selectedCourse && currentLecture) {
        const isAlreadyCompleted = selectedCourse.progress?.doneLectures?.[currentLecture.id] ?? false;
        if (!isAlreadyCompleted) {
          void toggleLectureCompletion(selectedCourse.id, currentLecture.id);
        }
        hasCompletedRef.current = true;
      }
    }
  }, [setPlayingState, selectedCourse, currentLecture, toggleLectureCompletion]);

  useEffect(() => {
    if (selectedCourse) {
      setCurrentCourse(selectedCourse);
    }
  }, [selectedCourse?.id, selectedCourse?.sections?.length, setCurrentCourse]);

  useEffect(() => {
    if (!selectedCourse) {
      reset();
    }
  }, [selectedCourse, reset]);

  useEffect(() => {
    setPlayerRef(playerRef);
  }, [setPlayerRef]);

  // Reset completion state when lecture changes
  useEffect(() => {
    hasCompletedRef.current = false;
  }, [currentLecture?.id]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    currentPlaybackId,
    muxTokens,
    currentLecture,
    currentSectionId,
    isVideoLoading,
    playerRef,
    shouldAutoplay,
    handleTimeUpdate: handlePlayerTimeUpdate,
    loadLecture,
    isPlaying: () => isPlaying,
  };
};
